import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useText } from "../../clients/words";
import { novayaPerepiska, novayaPerepiskaKto } from "../api";
import { useZaderzhka } from "../hooks";
import { Shtorka } from "./Razbor";

/**
 * «НАПИСАТЬ НОВОЙ» — окно начала переписки по номеру (владелец, 24.09.2026:
 * «в обращениях НЕТ кнопки начать диалог с новым клиентом с кем нет
 * переписки»; картинка `preview.opsmind.ru/state/nk`, «+» владельца 24.09).
 *
 * Номер → кто это (сервер ничего не заводит) → кнопка линии → разговор открыт.
 * ⛔ Выбора шаблона здесь нет: у новой клиентки окно WhatsApp закрыто, и
 * открывшийся разбор сам ставит «Шаблон» вместо поля ответа — второй лист
 * шаблонов разошёлся бы с первым.
 */
export function NapisatNovoy({
  onZakryt,
  onOtkryt,
}: {
  onZakryt: () => void;
  onOtkryt: (id: number) => void;
}) {
  const text = useText();
  const qc = useQueryClient();
  const [telefon, setTelefon] = useState("");
  const otlozhennyy = useZaderzhka(telefon.trim());
  const cifr = otlozhennyy.replace(/\D/g, "").length;

  const kto = useQuery({
    queryKey: ["obrashcheniya", "novaya-perepiska", otlozhennyy],
    queryFn: () => novayaPerepiskaKto(otlozhennyy),
    // Номер проверяет сервер (общий нормализатор); спрашиваем, когда цифр набрано на номер.
    enabled: cifr >= 10,
    retry: false,
  });

  const napisat = useMutation({
    mutationFn: (channelId: number) => novayaPerepiska(telefon.trim(), channelId),
    onSuccess: (razbor) => {
      void qc.invalidateQueries({ queryKey: ["obrashcheniya"] });
      onZakryt();
      onOtkryt(razbor.id);
    },
  });

  const otkaz = (oshibka: unknown): string | null => {
    const otvet = (oshibka as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
      ?.response?.data;
    return otvet?.errors?.telefon?.[0] ?? otvet?.message ?? null;
  };

  const dannye = kto.data;
  const klientka = dannye?.klientka ?? null;
  const oshibka = (kto.isError ? otkaz(kto.error) : null) ?? (napisat.isError ? otkaz(napisat.error) : null);

  return (
    <Shtorka zagolovok={text("obrashcheniya.novaya.knopka")} onZakryt={onZakryt}>
      <label className="mb-1.5 block text-ops-caption text-ops-text-tertiary" htmlFor="napisat-novoy-telefon">
        {text("obrashcheniya.novaya.telefon")}
      </label>
      <input
        id="napisat-novoy-telefon"
        autoFocus
        type="tel"
        inputMode="tel"
        value={telefon}
        onChange={(e) => setTelefon(e.target.value)}
        placeholder="+7 900 000-00-00"
        className="w-full rounded-ops-pill border border-ops-border px-4 py-2 text-ops-body outline-none focus:border-ops-primary"
      />

      {oshibka && <p className="mt-3 text-ops-caption text-ops-danger">{oshibka}</p>}

      {dannye && !kto.isError && (
        <>
          <p className="mt-3 rounded-ops-card bg-ops-surface-2 px-3 py-2.5 text-ops-caption text-ops-text">
            {klientka
              ? [
                  klientka.imya,
                  klientka.posledniy_vizit
                    ? `${text("obrashcheniya.novaya.byla")} ${klientka.posledniy_vizit}`
                    : null,
                  text(
                    klientka.perepiska_byla
                      ? "obrashcheniya.novaya.perepiska_byla"
                      : "obrashcheniya.novaya.perepiski_ne_bylo",
                  ),
                ]
                  .filter(Boolean)
                  .join(" · ")
              : text("obrashcheniya.novaya.novaya_klientka")}
          </p>

          <p className="mb-2 mt-3.5 text-ops-caption text-ops-text-tertiary">
            {text("obrashcheniya.novaya.otkuda")}
          </p>

          {dannye.linii.length === 0 && (
            <p className="mb-2 text-ops-caption text-ops-text">{text("obrashcheniya.novaya.net_linii")}</p>
          )}

          {dannye.linii.map((l) => (
            <button
              key={l.channel_id}
              type="button"
              disabled={napisat.isPending}
              onClick={() => napisat.mutate(l.channel_id)}
              className="mb-2 w-full rounded-ops-pill bg-ops-primary px-4 py-2.5 text-ops-body font-medium text-ops-on-primary disabled:opacity-60"
            >
              {text("obrashcheniya.novaya.napisat")} · {l.title}
            </button>
          ))}

          {dannye.cherez_amo && (
            <button
              type="button"
              disabled
              className="w-full rounded-ops-pill border border-ops-border px-4 py-2.5 text-ops-body text-ops-text-tertiary"
            >
              {text("obrashcheniya.novaya.cherez_amo")}
            </button>
          )}
        </>
      )}
    </Shtorka>
  );
}
